import { useDebounceFn } from 'ahooks';
import {
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Tooltip,
} from 'antd';
import { useAppDispatch } from 'app/hooks';
import classnames from 'classnames';
import { RFQTableWarningIcon, RFQWarningIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import {
  DEFAULT_DATE_FORMAT,
  RFQ_SHIPPING_DATE_FORMAT_LOCAL,
} from 'components/RevxDate';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxMoney } from 'components/RevxMoney';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import {
  JobRFQDraftOrgItemsViewer,
  JobRFQDraftQtyItemsViewer,
  JobRFQDraftShippingAdjustParams,
  JobRFQDraftShippingItemsAccountSelect,
  JobRFQDraftShippingItemsViewer,
  JobRFQViewer,
  RFQOrgStatusEnum,
  ShippingAccountTypeEnum,
} from 'domains/jobconfigDomain';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { JobItem, JobParticipantEnum } from 'domains/workspaceDomain';
import { QuantityItemColor } from 'features/JobConfig/components/QuantityPriceItem';
import { clone } from 'lodash-es';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import workspaceService from 'services/jobService';
import { addressTranslator } from 'utils/address';
import { DEBOUNCE_TIME } from 'utils/constants';
import { formatNumberToThousandths } from 'utils/number';

const { Nonstandard } = CarrierTypeEnum;

type Props = {
  data?: JobRFQViewer;
  jobData: JobItem;
  determineShippingUpdateData: () => void;
  reloadQuoteDate: (jump?: boolean) => void;
};

const colors: QuantityItemColor[] = [
  'yellow',
  'purple',
  'green',
  'steelBlue',
  'bisque',
];

type LoadingList = {
  [id: number]: boolean;
};

type UpdateKeyList = {
  [id: number]: number;
};

type PopoverList = {
  [key: string]: { show: boolean };
};

type NoteList = {
  [orgId: number]: string;
};

const TabDetermineShipping = ({
  data,
  jobData,
  determineShippingUpdateData,
  reloadQuoteDate,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [determineTableLoading, setDetermineTableLoading] =
    useState<LoadingList>();
  const [determineTableKey, setDetermineTableKey] = useState<UpdateKeyList>();
  const [draftOrgItems, setDraftOrgItems] =
    useState<JobRFQDraftOrgItemsViewer[]>();
  const [popoverVisible, setPopoverVisible] = useState<PopoverList>();
  const [noteList, setNoteList] = useState<NoteList>();
  const correctingReadyDateRef = useRef(false);

  const autoCorrectPastReadyDates = async (
    orgItems: JobRFQDraftOrgItemsViewer[]
  ) => {
    if (correctingReadyDateRef.current) return false;
    const today = moment().startOf('day');
    const todayStr = today.format(RFQ_SHIPPING_DATE_FORMAT_LOCAL);
    const adjustTasks: Array<{
      orgId: number;
      qtySerialNumber: number;
      adjustParams: JobRFQDraftShippingAdjustParams;
    }> = [];

    orgItems.forEach((orgItem) => {
      orgItem.qtyItems.forEach((qtyItem) => {
        qtyItem.shippingItems.forEach((shippingItem) => {
          const readyDate = moment(
            shippingItem.readyDate,
            RFQ_SHIPPING_DATE_FORMAT_LOCAL
          );
          if (!readyDate.isValid() || !readyDate.isBefore(today, 'day')) return;

          const adjustParams: JobRFQDraftShippingAdjustParams = {
            serialNumber: shippingItem.shippingSerialNumber,
            readyDate: todayStr,
          };
          const deliveryDate = moment(
            shippingItem.deliveryDate,
            RFQ_SHIPPING_DATE_FORMAT_LOCAL
          );
          if (deliveryDate.isValid() && !today.isBefore(deliveryDate, 'day')) {
            adjustParams.deliveryDate = today
              .clone()
              .add(1, 'day')
              .format(RFQ_SHIPPING_DATE_FORMAT_LOCAL);
          }

          adjustTasks.push({
            orgId: orgItem.orgId,
            qtySerialNumber: qtyItem.qtySerialNumber,
            adjustParams,
          });
        });
      });
    });

    if (!adjustTasks.length) return false;

    correctingReadyDateRef.current = true;
    try {
      await Promise.all(
        adjustTasks.map((task) =>
          workspaceService.adjustRfqJobDraftShipping({
            jobId: data!.id,
            orgId: task.orgId,
            qtySerialNumber: task.qtySerialNumber,
            adjustParams: task.adjustParams,
          })
        )
      );
      return true;
    } finally {
      correctingReadyDateRef.current = false;
    }
  };

  useEffect(() => {
    getDraftData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const getDraftData = async () => {
    await dispatch.app.showLoading();
    let v = await workspaceService.getRfqJobDraft(data!.id);
    const corrected = await autoCorrectPastReadyDates(v.orgItems || []);
    if (corrected) {
      v = await workspaceService.getRfqJobDraft(data!.id);
    }
    const loadingList: LoadingList = {},
      keyList: UpdateKeyList = {},
      popoverList: PopoverList = {},
      noteList: NoteList = {};
    v.orgItems.forEach((i, index) => {
      v.orgItems[index].currentSelectIndex = 0;
      const { orgId, sourcerNote } = i;
      draftOrgItems?.forEach((u) => {
        if (u.orgId === orgId) {
          v.orgItems[index].currentSelectIndex = u.currentSelectIndex;
        }
      });
      loadingList[orgId] = false;
      keyList[orgId] = 1;
      noteList[orgId] = sourcerNote || '';
      v.orgItems[index].qtyItems.forEach((u) => {
        u.shippingItems.forEach((z, index) => {
          popoverList[generatePopoverId(z)] = { show: false };
        });
      });
    });
    setDetermineTableLoading(loadingList);
    setDetermineTableKey(keyList);
    setDraftOrgItems(v.orgItems);
    setPopoverVisible(popoverList);
    setNoteList(noteList);
    await dispatch.app.hideLoading();
  };

  useEffect(() => {
    if (draftOrgItems) {
      const viewedList = draftOrgItems
        .map((i) => {
          const { orgId, qtyItems } = i;
          return {
            orgId,
            qtySerialNumber: qtyItems[0].qtySerialNumber,
            viewed: qtyItems[0].viewed,
          };
        })
        .filter((v) => !v.viewed);
      if (!!viewedList.length) {
        setShippingViewStatus(viewedList);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftOrgItems]);

  const { run: noteChangeHandler, cancel: cancelNoteChangeHandler } =
    useDebounceFn(
      async (orgId: number, note: string) => {
        await dispatch.app.showLoading();
        await workspaceService.addRfqJobDraftNote(data!.id, orgId, note);
        await dispatch.app.hideLoading();
      },
      { wait: DEBOUNCE_TIME }
    );

  const adjustShipping = async (
    record: JobRFQDraftShippingItemsViewer,
    adjustParams: Partial<JobRFQDraftShippingAdjustParams>
  ) => {
    const { shippingSerialNumber, orgId, qtySerialNumber } = record;
    const params = Object.assign(
      { serialNumber: shippingSerialNumber },
      adjustParams
    );
    return await workspaceService.adjustRfqJobDraftShipping({
      jobId: data!.id,
      orgId: orgId,
      qtySerialNumber: qtySerialNumber,
      adjustParams: params,
    });
  };

  const updateShippingItem = async (
    record: JobRFQDraftShippingItemsViewer,
    adjustParams: Partial<JobRFQDraftShippingAdjustParams>
  ) => {
    const { orgId: shippingOrgId } = record;
    try {
      /*setDetermineTableLoading({
        ...determineTableLoading,
        ...{ [record.orgId]: true },
      });*/
      await dispatch.app.showLoading();
      const shippingData = await adjustShipping(record, adjustParams);
      const cloneData = clone(draftOrgItems);
      cloneData!.forEach((i, orgIndex) => {
        const { orgId } = i;
        if (orgId === shippingOrgId) {
          i.qtyItems.forEach((item, qtyIndex) => {
            const { qtySerialNumber } = item;
            if (qtySerialNumber === record.qtySerialNumber) {
              if (cloneData?.[orgIndex]) {
                const { shippingSerialNumber } = record;
                const cache: JobRFQDraftShippingItemsViewer =
                  cloneData[orgIndex].qtyItems[qtyIndex].shippingItems[
                    shippingSerialNumber
                  ];
                cloneData[orgIndex].qtyItems[qtyIndex].shippingItems[
                  shippingSerialNumber
                ] = shippingData || clone(cache);
              }
            }
          });
        }
      });
      setDraftOrgItems(cloneData);
      await getDraftData();
    } catch {
    } finally {
      updateActiveKey(shippingOrgId);
      /*setDetermineTableLoading({
        ...determineTableLoading,
        ...{ [shippingOrgId]: false },
      });*/
      await dispatch.app.hideLoading();
    }
  };

  const updateActiveKey = (orgId: number) => {
    if (determineTableKey?.[orgId]) {
      setDetermineTableKey({
        ...determineTableKey,
        ...{ [orgId]: determineTableKey[orgId] + 1 },
      });
    }
  };

  const generatePopoverId = ({
    orgId,
    qtySerialNumber,
    shippingSerialNumber,
  }: JobRFQDraftShippingItemsViewer) => {
    return `${orgId}-${qtySerialNumber}-${shippingSerialNumber}`;
  };

  const toShippingDateMoment = (dateStr?: string) => {
    if (!dateStr) return moment.invalid();
    const datePart = String(dateStr).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (datePart) {
      return moment(datePart, RFQ_SHIPPING_DATE_FORMAT_LOCAL);
    }
    return moment(dateStr);
  };

  const getDetermineShippingColumn = (qtyItem?: JobRFQDraftQtyItemsViewer): TableColumnsType<JobRFQDraftShippingItemsViewer> =>
    [
      {
        title: 'Address',
        dataIndex: 'toName',
        width: 170,
        render: (value, record, index) => {
          return (
            <RevxFlexLayout wrap="wrap">
              <span>{value}</span>
              <span>{addressTranslator.dataToAddress(record)}</span>
            </RevxFlexLayout>
          );
        },
      },
      {
        title: 'Quantity',
        dataIndex: 'itemQuantity',
        width: 50,
        render: (value, record, index) => {
          return formatNumberToThousandths(value);
        },
      },
      {
        title: (record) => {
          return (
            <div>
              <p>
                Ready Date/
                <br />
                Ship Date
              </p>
            </div>
          );
        },
        dataIndex: 'readyDate',
        width: 150,
        render: (value, record, index) => {
          return (
            <div>
              <DatePicker
                disabled={index > 0}
                defaultValue={toShippingDateMoment(value)}
                format={DEFAULT_DATE_FORMAT}
                allowClear={false}
                disabledDate={(current) => {
                  return current < moment().startOf('day');
                }}
                dateRender={(current) => {
                  const isRecommended =
                    current >= moment().startOf('day') &&
                    current <= moment(record.originalDeliveryDate);
                  return (
                    <div className="ant-picker-cell-inner">
                      {current.date()}
                      {isRecommended && (
                        <span
                          style={{
                            color: '#faad14',
                            fontSize: 10,
                            position: 'absolute',
                            top: -2,
                            right: -4,
                            lineHeight: 1,
                          }}
                        >
                          ★
                        </span>
                      )}
                    </div>
                  );
                }}
                onOpenChange={async (open) => {
                  if (!open) return;
                  const today = moment().startOf('day');
                  const currentReadyDate = toShippingDateMoment(
                    record.readyDate
                  );
                  if (
                    !currentReadyDate.isValid() ||
                    !currentReadyDate.startOf('day').isBefore(today, 'day')
                  ) {
                    return;
                  }
                  const params: Partial<JobRFQDraftShippingAdjustParams> = {
                    readyDate: today.format(RFQ_SHIPPING_DATE_FORMAT_LOCAL),
                    manualOverrideReadyDate: true,
                  };
                  const deliveryDate = toShippingDateMoment(
                    record.deliveryDate
                  );
                  if (deliveryDate.isValid() && !today.isBefore(deliveryDate, 'day')) {
                    params.deliveryDate = today
                      .clone()
                      .add(1, 'day')
                      .format(RFQ_SHIPPING_DATE_FORMAT_LOCAL);
                  }
                  await updateShippingItem(record, params);
                }}
                onChange={async (v) => {
                  if (!v) return;
                  const currentDeliveryDate = toShippingDateMoment(
                    record.deliveryDate
                  );
                  const isOutsideRecommended =
                    currentDeliveryDate.isValid() &&
                    v.isAfter(currentDeliveryDate.endOf('day'));
                  const updateReadyDate = async (manualOverrideReadyDate: boolean) => {
                    const params: Partial<JobRFQDraftShippingAdjustParams> = {
                      readyDate: moment(v).format(
                        RFQ_SHIPPING_DATE_FORMAT_LOCAL
                      ),
                      manualOverrideReadyDate,
                    };
                    if (
                      currentDeliveryDate.isValid() &&
                      (v.isSame(currentDeliveryDate, 'day') ||
                        v.isAfter(currentDeliveryDate, 'day'))
                    ) {
                      params.deliveryDate = moment(v)
                        .add(1, 'day')
                        .format(RFQ_SHIPPING_DATE_FORMAT_LOCAL);
                    }
                    await updateShippingItem(record, params);
                  };
                  if (isOutsideRecommended) {
                    Modal.confirm({
                      title: 'Date outside recommended range',
                      content:
                        'The selected date is outside the system recommended range (today to delivery date). Are you sure you want to proceed?',
                      onOk: () => updateReadyDate(true),
                      onCancel: async () => {
                        updateActiveKey(record.orgId)
                      },
                    });
                  } else {
                    await updateReadyDate(false);
                  }
                }}
              />
            </div>
          );
        },
      },
      {
        title: 'Shipping Account',
        dataIndex: 'accountType',
        width: 150,
        render: (value, record, index) => {
          const { accountType } = record;
          const label = (value: JobRFQDraftShippingItemsAccountSelect) => {
            const { accountType } = value;
            if (accountType === ShippingAccountTypeEnum.SUPPLIER) {
              return 'Supplier';
            } else {
              return value.company;
            }
          };
          const isOneAccountType = record.accountTypeSelects.length === 1;
          const accountTypeListHas = record.accountTypeSelects
            .map((value) => {
              return value.accountType;
            })
            .includes(accountType);
          const disabledStatus = isOneAccountType && accountTypeListHas;
          const accountTypeValue =
            isOneAccountType && !accountTypeListHas ? undefined : accountType;
          return (
            <Select
              className="shipment-account-select"
              defaultValue={accountTypeValue}
              disabled={disabledStatus}
              onChange={async (accountType) => {
                await updateShippingItem(record, { accountType });
              }}
            >
              {record.accountTypeSelects.map((value, index) => {
                return (
                  <Select.Option value={value.accountType} key={index}>
                    {label(value)}
                  </Select.Option>
                );
              })}
            </Select>
          );
        },
      },
      {
        title: 'Shipping Method',
        dataIndex: 'carrierType',
        width: 200,
        render: (value, record, index) => {
          const { carrierServiceSelects, carrierService, qtySerialNumber } =
            record;
          const defaultValue = carrierServiceSelects.find(
            (select) => select.serviceName === carrierService
          );
          return (
            <>
              <Select
                className="shipment-method-select"
                defaultValue={defaultValue?.serviceName}
                key={qtySerialNumber}
                onChange={async (v) => {
                  const [service, accountType, carrierType] = v.split(',');
                  await updateShippingItem(record, {
                    service,
                    accountType: accountType as ShippingAccountTypeEnum,
                    carrierType: carrierType as CarrierTypeEnum,
                  });
                }}
              >
                {carrierServiceSelects.map((i, index) => {
                  return (
                    <Select.Option
                      value={[i.serviceName, i.accountType, i.carrierType].join(
                        ','
                      )}
                      key={`${qtySerialNumber}-${index}`}
                    >
                      {i.serviceName}
                    </Select.Option>
                  );
                })}
              </Select>
            </>
          );
        },
      },
      {
        title: 'Arrival Date',
        dataIndex: 'deliveryDate',
        width: 150,
        render: (value, record, index) => {
          const {
            deliveryDate,
            readyDate,
            deliveryDateExceededAck,
            deliveryDateExceeded,
          } = record;
          const defaultValue = () => {
            return toShippingDateMoment(deliveryDate);
          };
          return (
            <RevxFlexLayout className="arrival-date-wrap">
              <DatePicker
                defaultPickerValue={toShippingDateMoment(readyDate)}
                defaultValue={defaultValue()}
                format={DEFAULT_DATE_FORMAT}
                allowClear={false}
                disabledDate={(current) => {
                  return current < toShippingDateMoment(readyDate);
                }}
                onChange={async (v) => {
                  await updateShippingItem(record, {
                    deliveryDate: moment(v).format(
                      RFQ_SHIPPING_DATE_FORMAT_LOCAL
                    ),
                  });
                }}
              />
              {deliveryDateExceeded && !deliveryDateExceededAck && (
                <Popover
                  trigger={['hover']}
                  arrowPointAtCenter={true}
                  color="#f3f3f3"
                  visible={popoverVisible?.[generatePopoverId(record)]?.show}
                  placement="bottom"
                  getPopupContainer={(e) => e.parentNode as HTMLElement}
                  content={() => {
                    return (
                      <RevxFlexLayout
                        className="arrival-date-tips"
                        justify="space-between"
                      >
                        {t('workspace.rfq.arrivalDateTips')}
                        <div className="action">
                          <RevxButton
                            onClick={() => {
                              setPopoverVisible({
                                ...popoverVisible,
                                ...{
                                  [generatePopoverId(record)]: { show: false },
                                },
                              });
                            }}
                          >
                            {t('common.no')}
                          </RevxButton>
                          <RevxButton
                            onClick={async () => {
                              await updateShippingItem(record, {
                                deliveryDateExceededAck: true,
                              });
                              setPopoverVisible({
                                ...popoverVisible,
                                ...{
                                  [generatePopoverId(record)]: { show: false },
                                },
                              });
                            }}
                          >
                            {t('common.yes')}
                          </RevxButton>
                        </div>
                      </RevxFlexLayout>
                    );
                  }}
                >
                  <RevxFlexLayout
                    onClick={() => {
                      setPopoverVisible({
                        ...popoverVisible,
                        ...{ [generatePopoverId(record)]: { show: true } },
                      });
                    }}
                  >
                    <RFQTableWarningIcon />
                  </RevxFlexLayout>
                </Popover>
              )}
            </RevxFlexLayout>
          );
        },
      },
      {
        title: (record) => {
          return (
            <div>
              <p>
                Requested
                <br />
                Delivery Date
              </p>
            </div>
          );
        },
        dataIndex: 'expectedDeliveryDate',
        width: 100,
        render: (value, record, index) => {
          return moment(value).format(DEFAULT_DATE_FORMAT);
        },
      },
      {
        title: 'Shipping Price',
        dataIndex: 'price',
        render: (value, record, index) => {
          const { accountType } = record;
          const isSpecial =
            accountType === ShippingAccountTypeEnum.ORGANIZATION &&
            !(
              jobData?.participants?.length === 1 &&
              jobData?.participants.includes(JobParticipantEnum.PRODUCER)
            );
          const showRecommend =
            qtyItem?.manualOverrideReadyDate &&
            qtyItem?.recommendShippingPrice != null &&
            qtyItem?.recommendShippingPrice !== value;
          return (
            <div>
              <RevxMoney value={value} />
              {showRecommend && (
                <Tooltip title={<>Recommended Price: <RevxMoney value={qtyItem.recommendShippingPrice} /></>} overlayStyle={{ whiteSpace: 'nowrap' }}>
                  <span style={{ marginLeft: 4, cursor: 'pointer' }}><RFQTableWarningIcon /></span>
                </Tooltip>
              )}
              {isSpecial && '(billed separately)'}
            </div>
          );
        },
      },
    ];

  const setRfqJobDraftViewedStatusHandler = async (
    v: { orgId: number; qtySerialNumber: number }[]
  ) => {
    await workspaceService.setRfqJobDraftViewedStatus({
      jobId: data!.id,
      viewedParams: v,
    });
  };

  const setShippingViewStatus = async (
    v: { orgId: number; qtySerialNumber: number }[] | [],
    qty?: {
      qtyItem: JobRFQDraftQtyItemsViewer;
      orgItemIndex: number;
      qtyItemIndex: number;
    }
  ) => {
    if (!v.length) return;
    const cloneData = clone(draftOrgItems);
    if (qty && cloneData) {
      const { qtyItem, orgItemIndex, qtyItemIndex } = qty;
      if (!qtyItem.viewed) {
        await setRfqJobDraftViewedStatusHandler(v);
      }
      cloneData[orgItemIndex].qtyItems[qtyItemIndex].viewed = true;
      cloneData[orgItemIndex].currentSelectIndex = qtyItemIndex;
    } else if (cloneData) {
      await setRfqJobDraftViewedStatusHandler(v);
      cloneData.forEach((i, index) => {
        cloneData[index].qtyItems[0].viewed = true;
      });
    }
    setDraftOrgItems(cloneData);
  };

  const checkShippingStatus = (item: JobRFQDraftQtyItemsViewer) => {
    const { shippingItems } = item;
    let disabledStatus = false;
    shippingItems.forEach(
      ({
        carrierServiceSelects,
        carrierType,
        deliveryDateExceededAck,
        deliveryDateExceeded,
        shippingMethodAllowed,
      }) => {
        const isNonstandard = carrierType === Nonstandard;
        if (
          (!isNonstandard && !carrierServiceSelects.length) ||
          (deliveryDateExceeded && !deliveryDateExceededAck) ||
          !shippingMethodAllowed
        ) {
          disabledStatus = true;
        }
      }
    );
    return disabledStatus;
  };

  const sendRFQJobHandler = async (
    orgId: number,
    confirmation: boolean,
    jumpToResponses?: boolean
  ) => {
    await workspaceService.sendRfqJob(data!.id, orgId, confirmation);
    determineShippingUpdateData();
    await getDraftData();
    await reloadQuoteDate(jumpToResponses);
  };

  return (
    <div className="rfq-determine-shipping-table">
      {draftOrgItems?.length ? (
        <>
          {draftOrgItems.map((orgItem, orgItemIndex) => {
            const {
              orgName,
              orgId,
              recipients,
              qtyItems,
              currentSelectIndex,
              rfqOrgStatus,
            } = orgItem;
            let disabledStatus = false;
            qtyItems.forEach((qtyItem) => {
              const { viewed, shippingItems } = qtyItem;
              if (!viewed) {
                disabledStatus = true;
                return;
              }
              shippingItems.forEach((item) => {
                const {
                  carrierServiceSelects,
                  carrierType,
                  deliveryDateExceededAck,
                  deliveryDateExceeded,
                  shippingMethodAllowed,
                } = item;
                const isNonstandard = carrierType === Nonstandard;
                if (
                  (!isNonstandard && !carrierServiceSelects.length) ||
                  (deliveryDateExceeded && !deliveryDateExceededAck) ||
                  !shippingMethodAllowed
                ) {
                  disabledStatus = true;
                }
                item.orgId = draftOrgItems[orgItemIndex].orgId;
                item.orgName = draftOrgItems[orgItemIndex].orgName;
                item.qtySerialNumber = qtyItem.qtySerialNumber;
              });
            });
            return (
              <div className="determine-table" key={orgItemIndex}>
                <RevxFlexLayout
                  className="determine-table__top-bar"
                  justify="space-between"
                  align="center"
                >
                  <div className="determine-table__top-bar__info">
                    <div className="supplier-name">{orgName}</div>
                    <RevxFlexLayout className="address">
                      <p>
                        {addressTranslator.rfqDetermineShippingDataToAddress(
                          orgItem
                        )}
                      </p>
                      <Divider type="vertical" />
                      <p>
                        Contacts:{' '}
                        {recipients.length ? (
                          <>
                            {recipients.map((i, index) => {
                              if (!i.checked) {
                                return <></>;
                              }
                              const len = recipients.length;
                              return (
                                <span key={index}>{`${i.firstName || ''} ${
                                  i.lastName || ''
                                }${
                                  index !== len - 1 && len !== 1 ? ', ' : ''
                                }`}</span>
                              );
                            })}
                          </>
                        ) : (
                          'None'
                        )}
                      </p>
                    </RevxFlexLayout>
                  </div>
                  <RevxFlexLayout className="determine-table__top-bar__actions">
                    <div className="remove-button">
                      <Popconfirm
                        title="Are you sure？"
                        onConfirm={async () => {
                          await workspaceService.deleteRfqJobDraft(
                            data!.id,
                            orgId
                          );
                          determineShippingUpdateData();
                          await getDraftData();
                        }}
                      >
                        <RevxButton brandOrange={true} type="link">
                          {orgItem.draftFromUpdateShipping
                            ? t('common.cancel')
                            : t('common.remove')}
                        </RevxButton>
                      </Popconfirm>
                    </div>
                    {orgItem.draftFromUpdateShipping &&
                      rfqOrgStatus === RFQOrgStatusEnum.IN_PROCESS && (
                        <div className="send-rfq-button confirmation">
                          <RevxButton
                            type="ghost"
                            disabled={disabledStatus}
                            onClick={async () => {
                              await sendRFQJobHandler(orgId, true, true);
                            }}
                          >
                            {t('workspace.rfq.btn.sendRFQConfirmation')}
                          </RevxButton>
                        </div>
                      )}
                    <div className="send-rfq-button">
                      <RevxButton
                        disabled={disabledStatus}
                        onClick={async () => {
                          await sendRFQJobHandler(orgId, false);
                        }}
                      >
                        {orgItem.draftFromUpdateShipping
                          ? t('workspace.rfq.btn.resendRFQ')
                          : t('workspace.rfq.btn.sendRFQ')}
                      </RevxButton>
                    </div>
                  </RevxFlexLayout>
                </RevxFlexLayout>
                <RevxFlexLayout
                  className="determine-table__quantity"
                  justify="justify-start"
                >
                  <div className="determine-table__quantity__label">QTY: </div>
                  <RevxFlexLayout className="determine-table__quantity__items">
                    {qtyItems.map((qtyItem, qtyItemIndex) => (
                      <RevxFlexLayout
                        key={qtyItemIndex}
                        // className={`quantity-block workspace-quote-quantity-label ${colors[qtyItemIndex]}`}
                        className={classnames(
                          'quantity-block workspace-quote-quantity-label',
                          `${colors[qtyItemIndex]}`,
                          {
                            selected: currentSelectIndex === qtyItemIndex,
                          }
                        )}
                        onClick={async () => {
                          await setShippingViewStatus(
                            [
                              {
                                orgId,
                                qtySerialNumber: qtyItem.qtySerialNumber,
                              },
                            ],
                            { qtyItem, qtyItemIndex, orgItemIndex }
                          );
                        }}
                      >
                        {checkShippingStatus(qtyItem) && qtyItem.viewed ? (
                          <RFQWarningIcon />
                        ) : (
                          <Checkbox checked={qtyItem.viewed} />
                        )}
                        <span className="count">
                          {formatNumberToThousandths(qtyItem.quantity)}
                        </span>
                      </RevxFlexLayout>
                    ))}
                  </RevxFlexLayout>
                </RevxFlexLayout>
                <div className="determine-table__main">
                  <RevxTable<JobRFQDraftShippingItemsViewer>
                    dataSource={qtyItems[currentSelectIndex]?.shippingItems}
                    columns={getDetermineShippingColumn(qtyItems[currentSelectIndex])}
                    pagination={false}
                    rowKey="shippingSerialNumber"
                    key={`${currentSelectIndex}-${orgItemIndex}-${determineTableKey?.[orgId]}`}
                    loading={determineTableLoading?.[orgId]}
                  />
                </div>
                <div className="sourcer-notes">
                  <div className="sourcer-notes__title">Note to {orgName}</div>
                  <Input.TextArea
                    value={noteList?.[orgId]}
                    // defaultValue={noteList?.[orgId]}
                    onChange={async (e) => {
                      const v = e.target.value;
                      if (noteList) {
                        noteList[orgId] = v;
                        setNoteList(clone(noteList));
                        cancelNoteChangeHandler();
                        await noteChangeHandler(orgId, v);
                      }
                    }}
                    placeholder={t('workspace.rfq.inputNotes')}
                  />
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 20 }} />
      )}
    </div>
  );
};
export default TabDetermineShipping;
